import * as React from 'react';
import { SharedColors, Stack, Text } from '@fluentui/react';
import { IPiceaVerify, VerifyChecks, VerifyStatuses } from '@piceasoft/core';
import { strings } from '../../../../../localization/strings';
import { verifyDescriber, verifyStatusDescriber } from '../../../../../core/scripts/picea.describer';
import { errorDescriber } from '../../../../../core/scripts/picea.errors';

type ComponentResult = { name: string; check_succeeded: boolean; genuine?: boolean | null };
const order = ['Display', 'RearCamera', 'FrontCamera', 'TrueDepthCamera', 'Battery'];

export const getGenuineComponents = (item: IPiceaVerify): ComponentResult[] => {
    if (item.check !== VerifyChecks.GENUINE_COMPONENTS || !Array.isArray(item.data)) return [];
    return item.data.flatMap(entry => Array.isArray(entry?.value?.components) ?
        entry.value.components.filter((component: any): component is ComponentResult =>
            component != null && typeof component.name === 'string' && typeof component.check_succeeded === 'boolean'
        ) : []).sort((a, b) => {
            const rank = (name: string) => order.includes(name) ? order.indexOf(name) : order.length;
            return rank(a.name) - rank(b.name);
        });
};

export const getComponentStatus = (component: ComponentResult): 'GENUINE' | 'NOT_GENUINE' | 'NOT_DETERMINED' => {
    if (!component.check_succeeded || typeof component.genuine !== 'boolean') return 'NOT_DETERMINED';
    return component.genuine ? 'GENUINE' : 'NOT_GENUINE';
};

export const GenuineComponentResults: React.FC<{ item: IPiceaVerify }> = ({ item }) => {
    const components = getGenuineComponents(item);
    const labels = strings.PICEA.VERIFY.CHECKS.GENUINE_COMPONENTS_RESULT;
    const names: Record<string, string> = {
        Display: labels.DISPLA, RearCamera: labels.REAR_CAMERA, FrontCamera: labels.FRONT_CAMERA,
        TrueDepthCamera: labels.TRUE_DEPTH_CAMERA, Battery: labels.BATTERY,
    };
    const statusColor = item.status === VerifyStatuses.VerifyStatus_Passed ? SharedColors.green20 : SharedColors.orange10;

    return (
        <Stack>
            <Text variant="small" style={{ color: SharedColors.gray40, fontWeight: 'bold' }}>{verifyDescriber(item.check)}</Text>
            <Text style={{ color: statusColor }}>{verifyStatusDescriber(item.status)}</Text>
            {components.length > 0 && (
                <Stack role="table" aria-label={verifyDescriber(item.check)} tokens={{ childrenGap: 4 }}
                    styles={{ root: { marginLeft: 16, marginTop: 8, maxWidth: 420 } }}>
                    {components.map((component, index) => {
                        const status = getComponentStatus(component);
                        const color = status === 'GENUINE' ? SharedColors.green20 :
                            status === 'NOT_GENUINE' ? SharedColors.red10 : SharedColors.orange10;
                        return (
                            <Stack role="row" key={`${component.name}-${index}`} horizontal tokens={{ childrenGap: 16 }}>
                                <Text role="cell" styles={{ root: { color: SharedColors.gray40, flex: 1, minWidth: 0 } }}>{Object.prototype.hasOwnProperty.call(names, component.name) ? names[component.name] : component.name}</Text>
                                <Text role="cell" styles={{ root: { color, flex: 1, minWidth: 0 } }}>{labels[status]}</Text>
                            </Stack>
                        );
                    })}
                </Stack>
            )}
            {item.status === VerifyStatuses.VerifyStatus_Unknown && !!item.error_code && (
                <Text variant="xSmall" style={{ color: SharedColors.red10 }}>{errorDescriber(item.error_code)}</Text>
            )}
        </Stack>
    );
};
